import { useCallback } from 'react';
import { useLinkClick } from './useLinkClick';
import { AdobeAnalyticsConst } from '../adobeAnalyticsConstants/adobeAnalyticsConstants';

interface TabClickOptions {
  innertext: string;
  position: string;
  name: string;
}

interface SearchClickOptions {
  innertext: string;
  position?: string;
  name?: string;
}

export const useCentralizedLinkClick = () => {
  const { trackLinkClick } = useLinkClick();

  const createClickTracker = useCallback(
    (type: string) =>
      ({ innertext, position, name }: TabClickOptions) => {
        trackLinkClick({ innertext, type, position, name });
      },
    [trackLinkClick],
  );

  const trackTabClick = createClickTracker(AdobeAnalyticsConst.LINK_TYPES.TABS);
  const trackButtonClick = createClickTracker(AdobeAnalyticsConst.LINK_TYPES.BUTTON);

  const trackSearchClick = useCallback(
    ({
      innertext,
      position = AdobeAnalyticsConst.POSITIONS.APPLICATION_HEADER,
      name = AdobeAnalyticsConst.LINKCLICK_NAMES.SEARCH_HCPS,
    }: SearchClickOptions) => {
      trackLinkClick({
        innertext,
        type: AdobeAnalyticsConst.LINK_TYPES.BUTTON,
        position,
        name,
      });
    },
    [trackLinkClick],
  );

  const trackCoverageBySegmentationTabClick = useCallback(
    (tabName: string) => {
      trackTabClick({
        innertext: tabName,
        position: AdobeAnalyticsConst.POSITIONS.COVERAGE_BY_SEGMENTATION_TABS,
        name: AdobeAnalyticsConst.LINKCLICK_NAMES.COVERAGE_BY_SEGMENTATION_TABS,
      });
    },
    [trackTabClick],
  );

  const trackFavoritesClick = useCallback(
    ({
      innertext = AdobeAnalyticsConst.BUTTON_TEXT.FAVORITES_NAVIGATION,
      position = AdobeAnalyticsConst.POSITIONS.APPLICATION_HEADER,
      name = AdobeAnalyticsConst.LINKCLICK_NAMES.FAVORITES_NAVIGATION,
    }: Partial<SearchClickOptions> = {}) => {
      trackLinkClick({
        innertext,
        type: AdobeAnalyticsConst.LINK_TYPES.BUTTON,
        position,
        name,
      });
    },
    [trackLinkClick],
  );

  return {
    trackLinkClick,
    trackTabClick,
    trackButtonClick,
    trackSearchClick,
    trackCoverageBySegmentationTabClick,
    trackFavoritesClick,
  };
};
